import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, LinearProgress, Typography, Box, TablePagination
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import axios from 'axios';
import JobFilter from './JobFilter';
import DetailsModal from './detailsmodal';


const JobTable = () => {
  const columns = [
    "id", "type", "status", "inserted", "failed", "errorMessage",
     "startedAt", "endedAt", "Details"
  ];

  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedFilters, setExpandedFilters] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const statusTypes = ['pending', 'working', 'completed', 'failed', 'cancelled'];
  const jobTypes = ['type1', 'type2', 'type3'];
  const statusColors = {
    pending: 'orange',
    working: 'blue',
    completed: 'green',
    failed: 'red',
    cancelled: 'grey'
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/v1/jobs?page=${page + 1}&limit=${rowsPerPage}`, { baseURL: '' });
      if (response.data && response.data.success) {
        setJobs(response.data.jobs || response.data.data || []);
        setTotalRecords(response.data.total || 0);
      } else {
        setError('Failed to fetch jobs');
      }
    } catch (err) {
      setError(err.message || 'Error fetching jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [page, rowsPerPage]);

  useEffect(() => {
    let filtered = jobs;

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(job =>
        Object.values(job).some(val =>
          val && val.toString().toLowerCase().includes(lowerSearch)
        )
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(job => job.type === typeFilter);
    }

    // Sort: working jobs on top
    filtered.sort((a, b) => {
      if (a.status === "working" && b.status !== "working") return -1;
      if (a.status !== "working" && b.status === "working") return 1;
      return 0;
    });

    setFilteredJobs(filtered);
  }, [jobs, searchTerm, statusFilter, typeFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Typography align="center" sx={{ mt: 3 }}>
        <LinearProgress />
        Loading jobs...
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ mt: 3 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <JobFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        statusTypes={statusTypes}
        jobTypes={jobTypes}
        expandedFilters={expandedFilters}
        setExpandedFilters={setExpandedFilters}
        statusColors={statusColors}
      />

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table aria-label="jobs table">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1976d2' }}>
              {columns.map((column) => (
                <TableCell
                  key={column}
                  sx={{ fontWeight: 'bold', color: '#fff', padding: '16px 9px' }}
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        <TableBody>
  {filteredJobs.length === 0 ? (
    <TableRow>
      <TableCell colSpan={columns.length} align="center">
        No job data available
      </TableCell>
    </TableRow>
  ) : (
    filteredJobs.map((job) => (
      <TableRow key={job.id}>
        {columns.map((column) => (
          <TableCell key={column}>
            {column === "status" && job.status === "working" ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Working
                </Typography>
                <LinearProgress color="primary" />
              </Box>
            ) : column === "Details" ? (
              <RemoveRedEyeIcon
                sx={{ cursor: "pointer", color: "#1976d2" }}
                onClick={() => {
                  setSelectedJob(job);
                  setModalOpen(true);
                }}
              />
            ) : (
              job[column] !== undefined && job[column] !== null
                ? job[column].toString()
                : ''
            )}
          </TableCell>
        ))}
      </TableRow>
    ))
  )}
</TableBody>

        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page"
        />
      </TableContainer>
      <DetailsModal 
        open={modalOpen} 
        onClose={() => setModalOpen(false)} 
        job={selectedJob} 
      />
    </Box>
  );
};

export default JobTable;
